/**
 * Alta silenciosa en maestros (proveedores/clientes) a partir de los Excel
 * convertidos a SUENLACE. Solo da de alta NIFs nuevos; nunca modifica fichas
 * existentes. Sin IA ni créditos.
 */
import { supabase } from "@/integrations/supabase/client";
import { normalizeNif } from "@/lib/customerMaster";

export interface MasterCandidate {
  taxId: string;
  description: string;
  accountCode: string;
  counterAccount: string;
  postalCode?: string;
  operationType?: string;
}

const isValidAccount = (a: string) => /^\d{6,12}$/.test(a);

/**
 * Agrupa por NIF y descarta: filas sin NIF/nombre/cuenta válida y cuentas
 * genéricas compartidas por varios NIF dentro del mismo archivo.
 */
export const collectMasterCandidates = (rows: MasterCandidate[]): MasterCandidate[] => {
  const nifsByAccount = new Map<string, Set<string>>();
  const byNif = new Map<string, MasterCandidate>();
  for (const r of rows) {
    const nif = normalizeNif(r.taxId);
    const account = (r.accountCode || "").trim();
    const name = (r.description || "").trim();
    if (nif.length < 5 || !name || !isValidAccount(account)) continue;
    if (!nifsByAccount.has(account)) nifsByAccount.set(account, new Set());
    nifsByAccount.get(account)!.add(nif);
    if (!byNif.has(nif)) {
      byNif.set(nif, { ...r, taxId: nif, description: name, accountCode: account });
    }
  }
  return [...byNif.values()].filter((c) => (nifsByAccount.get(c.accountCode)?.size ?? 0) === 1);
};

export const syncExcelMaster = async (
  kind: "suppliers" | "customers",
  companyId: string,
  rows: MasterCandidate[],
): Promise<number> => {
  const candidates = collectMasterCandidates(rows);
  if (candidates.length === 0) return 0;
  const table = kind === "suppliers" ? "supplier_master" : "customer_master";
  const { data, error } = await supabase
    .from(table)
    .select("tax_id, account_code")
    .eq("company_id", companyId);
  if (error) throw error;
  const knownNifs = new Set((data ?? []).map((d) => normalizeNif(d.tax_id)));
  const knownAccounts = new Set((data ?? []).map((d) => (d.account_code || "").trim()));
  const fresh = candidates.filter((c) => !knownNifs.has(c.taxId) && !knownAccounts.has(c.accountCode));
  if (fresh.length === 0) return 0;
  const payload = fresh.map((c) => {
    const base = {
      company_id: companyId,
      tax_id: c.taxId,
      description: c.description.toUpperCase(),
      account_code: c.accountCode,
      postal_code: c.postalCode || null,
      operation_type: c.operationType || "interior",
      rules: {},
    };
    return kind === "suppliers"
      ? { ...base, expense_account: isValidAccount(c.counterAccount) ? c.counterAccount : "" }
      : { ...base, sales_account: isValidAccount(c.counterAccount) ? c.counterAccount : "" };
  });
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { error: insErr } = await supabase.from(table).insert(payload as any);
  if (insErr) throw insErr;
  return fresh.length;
};
